import { FilteredCreditApplication, RadioButtonList, StepStateUpdater } from '@applyonline/shared'
import { formatCurrency } from '@if/core/utils'
import useTermPaymentsTable from '../../ViewCreditApplication/InitialApprobationPage/hooks'
import { WorksheetForm } from '../schema-hooks'
import { useTermAmountOptions } from '../../../data/credit-application-hooks'
import { useEffect } from 'react'
import { useController, useFormContext, useWatch } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

interface Props {
  soonestDeliveryOn: Date
  creditApp: FilteredCreditApplication
  promoTerm?: number
  codePromo?: string | null
  setStepState: StepStateUpdater
  setNext: () => void
}

const TermStep = ({ soonestDeliveryOn, creditApp, promoTerm, codePromo, setStepState, setNext }: Props) => {
  const { t } = useTranslation()
  const { control, setValue } = useFormContext<WorksheetForm>()

  const {
    field: { value, onChange, onBlur, ref: _ref, ...field },
  } = useController({ name: 'term', control })
  const finalAmount = useWatch({ control, name: 'amountRequested' })
  const firstPaymentOn = useWatch({ control, name: 'firstPaymentOn' })

  const [monthlyPaymentPerMonths, isLoadingTerms] = useTermPaymentsTable(creditApp, {
    firstPaymentOn,
    activationDate: soonestDeliveryOn,
    requestedAmount: Number(finalAmount),
  })
  const termOptions = useTermAmountOptions(monthlyPaymentPerMonths)

  if (codePromo) {
    if (promoTerm && monthlyPaymentPerMonths.find((to) => to.term === promoTerm)) {
      setNext()
    }
  }
  useEffect(() => setStepState({ loading: isLoadingTerms }), [isLoadingTerms, setStepState])

  const showPromoCodeInvalidMessage =
    codePromo && (!promoTerm || !monthlyPaymentPerMonths.find((to) => to.term === promoTerm))
  const handleSetNext = () => {
    if (showPromoCodeInvalidMessage) {
      setValue('merchantPaymentPlanId', null)
    }
    setNext()
  }

  if (isLoadingTerms) return <div className="loader" />

  return (
    <>
      {showPromoCodeInvalidMessage && (
        <h3 style={{ marginBottom: '16px' }}>
          <Trans
            i18nKey="worksheet.term.promoCodeTermInvalid"
            values={{
              phoneNumber: '1-888-689-9876',
            }}
            components={{ a: <Link to="tel:18886899876" style={{ textWrap: 'nowrap' }} /> }}
          />
        </h3>
      )}
      <h3 style={{ marginBottom: '16px' }}>
        <Trans i18nKey="worksheet.term.title" values={{ finalAmount: formatCurrency(finalAmount) }} />
      </h3>
      {termOptions.length > 0 && (
        <>
          <p>{t('worksheet.term.description')}</p>
          <div className="paragraph">
            <div className="radiobuttons-stacked-container">
              <RadioButtonList
                {...field}
                onChange={(e) => {
                  onChange(parseInt(e.currentTarget.value, 10))
                  onBlur()
                  handleSetNext()
                }}
                onClick={() => {
                  onBlur()
                  handleSetNext()
                }}
                defaultValue={value.toString()}
                options={termOptions}
                reverse
              />
            </div>
          </div>
        </>
      )}
      {!termOptions.length && (
        <div className="paragraph">
          <p style={{ textAlign: 'center' }}>
            <Trans
              i18nKey="worksheet.term.empty"
              values={{
                phoneNumber: '1-888-689-9876',
              }}
              components={{ a: <Link to="tel:18886899876" style={{ textWrap: 'nowrap' }} /> }}
            />
          </p>
        </div>
      )}
    </>
  )
}

export default TermStep
